import { markResumeCompleted, readResumeState, removeResumeState, saveResumeState } from './activityIntelligence';

const VIDEO_COMPLETION_THRESHOLD = 0.95;
const MIN_VIDEO_RESUME_SECONDS = 10;

const buildVideoEntity = (video = {}, fallbackId = null, kind = 'video') => {
  const id = video.youtube_id || video.video_id || video.id || fallbackId;
  if (!id) return null;
  return {
    id: String(id),
    type: kind === 'video_mix' ? 'video_mix' : 'video',
    title: video.title || 'Rastyle Video',
    summary: video.description,
    media: video.thumbnail ? [{ kind: 'image', url: video.thumbnail }] : [],
  };
};

const findVideoResume = (entity, items = readResumeState()) => {
  if (!entity?.id) return null;
  return items.find((item) => String(item?.entity?.id) === String(entity.id)
    && ['video', 'video_mix'].includes(item?.entity?.type)) || null;
};

const normalizeVideoSample = ({ positionSeconds = 0, durationSeconds = 0 } = {}) => {
  const position = Math.max(0, Number(positionSeconds) || 0);
  const duration = Math.max(0, Number(durationSeconds) || 0);
  return { position, duration, progress: duration > 0 ? Math.min(1, position / duration) : 0 };
};

const persistVideoSample = ({ entity, positionSeconds, durationSeconds, metadata = {} } = {}) => {
  if (!entity?.id) return { saved: false, completed: false };
  const sample = normalizeVideoSample({ positionSeconds, durationSeconds });
  if (sample.duration <= 0 || sample.position < MIN_VIDEO_RESUME_SECONDS) {
    return { saved: false, completed: false, ...sample };
  }
  if (sample.progress >= VIDEO_COMPLETION_THRESHOLD) {
    markResumeCompleted(entity);
    return { saved: true, completed: true, ...sample };
  }
  saveResumeState(entity, sample.position, sample.duration, metadata);
  return { saved: true, completed: false, ...sample };
};

const restoreNativeVideoPosition = (videoElement, entity) => {
  const item = findVideoResume(entity);
  if (!videoElement || !item || item.position_seconds < MIN_VIDEO_RESUME_SECONDS) return 0;
  const duration = Number(videoElement.duration || item.duration_seconds || 0);
  const target = duration > 0 ? Math.min(item.position_seconds, Math.max(0, duration - 1)) : item.position_seconds;
  try {
    videoElement.currentTime = target;
    return target;
  } catch {
    return 0;
  }
};

const restoreYouTubePosition = (player, entity) => {
  const item = findVideoResume(entity);
  if (!player || !item || item.position_seconds < MIN_VIDEO_RESUME_SECONDS || typeof player.seekTo !== 'function') return 0;
  try {
    const duration = typeof player.getDuration === 'function' ? Number(player.getDuration() || item.duration_seconds || 0) : Number(item.duration_seconds || 0);
    const target = duration > 0 ? Math.min(item.position_seconds, Math.max(0, duration - 1)) : item.position_seconds;
    player.seekTo(target, true);
    return target;
  } catch {
    return 0;
  }
};

const startVideoOver = ({ player, videoElement, entity } = {}) => {
  if (entity?.id) removeResumeState(entity);
  try {
    if (player && typeof player.seekTo === 'function') player.seekTo(0, true);
    if (videoElement) videoElement.currentTime = 0;
  } catch {
    // Start-over feedback should never interrupt playback.
  }
  return 0;
};

export {
  MIN_VIDEO_RESUME_SECONDS,
  VIDEO_COMPLETION_THRESHOLD,
  buildVideoEntity,
  findVideoResume,
  normalizeVideoSample,
  persistVideoSample,
  restoreNativeVideoPosition,
  restoreYouTubePosition,
  startVideoOver,
};